/**
 * «Ver relaciones» de Emitidos (spec 2026-10-11 §3.3): el árbol de cada factura con sus pagos, notas de crédito y
 * demás CFDI relacionados, el resumen «2 pagos · 1 nota» junto al folio y el saldo pendiente (window.FDRelaciones).
 * Funciones puras que arman HTML ya escapado. Los datos llegan de v2/documentos/relaciones_lote_ajax ya juntados
 * y sin repetidos (Cfdi_arbol_model::juntar): aquí solo se reparten en secciones, se ordenan y se pintan.
 */
import { esc, dinero, fechaCorta, normalizarTipo, sinImporte, textoConteo } from './pestanas.js';

/** La nota del árbol sin el permiso de Cobranza (Cfdi_arbol_model::sin_cobros, revisión final M1). */
export const NOTA_SIN_COBROS = 'Los montos de cobro se ven con el permiso de Cobranza.';

/** c_TipoRelacion de CFDI 4.0 (01–07): el nombre del catálogo, para el título al pasar el mouse. */
export const RELACION = {
  '01': 'Nota de crédito de los documentos relacionados',
  '02': 'Nota de débito de los documentos relacionados',
  '03': 'Devolución de mercancía sobre facturas o traslados previos',
  '04': 'Sustitución de los CFDI previos',
  '05': 'Traslados de mercancías facturados previamente',
  '06': 'Factura generada por los traslados previos',
  '07': 'CFDI por aplicación de anticipo',
};
/** Cómo se lee cada relación desde la factura de la fila: «sale» = ella la declara; «entra» = otro CFDI la declara. */
const ETIQUETA = {
  '01': { sale: 'Nota de crédito de', entra: 'Nota de crédito' },
  '02': { sale: 'Nota de débito de', entra: 'Nota de débito' },
  '03': { sale: 'Devolución de', entra: 'Devolución' },
  '04': { sale: 'Sustituye a', entra: 'Sustituida por' },
  '05': { sale: 'Traslado de la factura', entra: 'Traslado relacionado' },
  '06': { sale: 'Factura de los traslados', entra: 'Facturado en' },
  '07': { sale: 'Aplica el anticipo', entra: 'Anticipo aplicado en' },
};

/** Valor de un catálogo por su clave PROPIA (`RELACION['constructor']` no es una relación). */
function propio(catalogo, clave) {
  return Object.prototype.hasOwnProperty.call(catalogo, clave) ? catalogo[clave] : undefined;
}
function cancelado(x) { return !!x && (x.estado === 0 || x.estado === '0'); }
function vacio(v) { return v === null || v === undefined || v === ''; }
function porFecha(a, b) {
  return String(a.fecha || '').localeCompare(String(b.fecha || ''))
    || ((Number(a.parcialidad) || 0) - (Number(b.parcialidad) || 0));
}
function plural(n, uno, varios) { return n + ' ' + (n === 1 ? uno : varios); }
function redondear(n) { return Math.round(n * 100) / 100; }
function folioDe(x) {
  const s = String((x && x.serie) || '') + String((x && x.folio) || '');
  if (s) return s;
  return (x && x.uuid) ? String(x.uuid).slice(0, 8).toUpperCase() : '—';
}

/** «Relacionado» a secas si no trae tipo de relación (llega como ''); con una clave que no conocemos, «Relacionado (08)». */
export function etiquetaRelacion(item) {
  const clave = item ? item.tipo_relacion : null;
  if (vacio(clave)) return 'Relacionado';
  const e = propio(ETIQUETA, clave);
  if (e) return e[item.sentido === 'sale' ? 'sale' : 'entra'];
  return 'Relacionado (' + String(clave) + ')';
}

/** Una nota de crédito de la factura: un Egreso que otro CFDI relaciona con 01 (spec §3.3). */
export function esNota(item) {
  return !!item && item.sentido === 'entra' && item.tipo_relacion === '01' && item.tipo === 'E';
}

/**
 * Saldo pendiente de una factura PPD vigente: el que dejó su último pago vigente (mayor parcialidad, luego la fecha
 * más reciente), si no el de su cobranza (cfdi.saldo_insoluto) y, sin nada, su total. null si no aplica (PUE,
 * cancelada, no es Ingreso).
 */
export function saldoPendiente(fila, pagos) {
  if (!fila || fila.tipodecomprobante !== 'I' || fila.metodopago !== 'PPD' || String(fila.estado_cfdi) === '0') return null;
  const conSaldo = (pagos || []).filter(function (p) {
    return !cancelado(p) && !vacio(p.saldo) && Number.isFinite(Number(p.saldo));
  });
  if (conSaldo.length) {
    const ultimo = conSaldo.slice().sort(function (a, b) {
      return ((Number(b.parcialidad) || 0) - (Number(a.parcialidad) || 0))
        || String(b.fecha || '').localeCompare(String(a.fecha || ''));
    })[0];
    return Math.max(0, redondear(Number(ultimo.saldo)));
  }
  const respaldos = [fila.saldo_insoluto, fila.monto_total];
  for (let i = 0; i < respaldos.length; i++) {
    if (!vacio(respaldos[i]) && Number.isFinite(Number(respaldos[i]))) return Math.max(0, redondear(Number(respaldos[i])));
  }
  return null;
}

/**
 * {pagos, notas, otros, saldo, moneda, sinCobros, mas} de una fila con lo que mandó el servidor ({pagos, relacionados}
 * y, si aplica, sin_cobros: sin el permiso de Cobranza, los REP llegan sin montos; mas: lo que pasó del tope por factura).
 */
export function armarArbol(fila, datos) {
  const d = datos || {};
  const pagos = (Array.isArray(d.pagos) ? d.pagos : []).slice().sort(porFecha);
  const rel = Array.isArray(d.relacionados) ? d.relacionados : [];
  const sinCobros = d.sin_cobros === true;
  const mas = Number(d.mas);
  return {
    pagos: pagos,
    notas: rel.filter(esNota).slice().sort(porFecha),
    otros: rel.filter(function (r) { return !esNota(r); }).slice().sort(porFecha),
    // El saldo pendiente sale de los cobros: sin el permiso de Cobranza no se enseña.
    saldo: sinCobros ? null : saldoPendiente(fila, pagos),
    moneda: String((fila && fila.moneda) || 'MXN'),
    sinCobros: sinCobros,
    mas: Number.isFinite(mas) && mas > 0 ? Math.trunc(mas) : 0,
  };
}

export function tieneRelaciones(arbol) {
  return !!arbol && (arbol.pagos.length + arbol.notas.length + arbol.otros.length) > 0;
}

/** «2 pagos · 1 nota · 1 relacionado · 1 cancelado»; '' sin relaciones (entonces no hay +). */
export function resumenRelaciones(arbol) {
  if (!tieneRelaciones(arbol)) return '';
  const todos = arbol.pagos.concat(arbol.notas, arbol.otros);
  const vivos = function (l) { return l.filter(function (x) { return !cancelado(x); }).length; };
  const partes = [];
  const pagos = vivos(arbol.pagos), notas = vivos(arbol.notas), otros = vivos(arbol.otros);
  const cancelados = todos.filter(cancelado).length;
  if (pagos) partes.push(plural(pagos, 'pago', 'pagos'));
  if (notas) partes.push(plural(notas, 'nota', 'notas'));
  if (otros) partes.push(plural(otros, 'relacionado', 'relacionados'));
  if (cancelados) partes.push(plural(cancelados, 'cancelado', 'cancelados'));
  if (arbol.mas) partes.push(textoConteo(arbol.mas) + ' más');
  return partes.join(' · ');
}

/**
 * El + junto al folio, con su resumen. '' sin resumen. El texto visible se recorta con puntos suspensivos si no
 * cabe en la columna (SCSS), así que el resumen completo va en el title y en el aria-label.
 */
export function botonRelaciones(id, resumen, abierto) {
  if (!resumen) return '';
  return '<button type="button" class="fd-rel-toggle" data-id="' + esc(id) + '" aria-expanded="' + (abierto ? 'true' : 'false')
    + '" aria-label="' + esc(resumen + ', relacionados con esta factura')
    + '" title="' + esc((abierto ? 'Ocultar' : 'Ver') + ' el detalle: ' + resumen) + '">'
    + '<i class="ti ' + (abierto ? 'ti-minus' : 'ti-plus') + '" aria-hidden="true"></i><span>' + esc(resumen) + '</span></button>';
}

/**
 * PDF, XML y «Ver en la tabla» de un pago o relacionado. Buscan el CFDI en la cuenta por su UUID: sin UUID (un cobro
 * sin complemento) o sin cfdi_id (el CFDI no está en Emitidos o es de otro modo) no habría a dónde llevar.
 */
function acciones(x, tipo) {
  if (!x.uuid || x.cfdi_id === null || x.cfdi_id === undefined) return '';
  const uuid = ' data-uuid="' + esc(x.uuid) + '"';
  return '<span class="fd-arbol-acciones">'
    + '<button type="button" class="btn btn-link btn-sm" data-fd-rel="pdf"' + uuid + '>PDF</button>'
    + (x.rfc ? '<button type="button" class="btn btn-link btn-sm" data-fd-rel="xml"' + uuid + ' data-rfc="' + esc(x.rfc) + '">XML</button>' : '')
    + '<button type="button" class="btn btn-link btn-sm" data-fd-rel="ir"' + uuid + ' data-tipo="' + esc(tipo || '') + '" data-fecha="'
    + esc(x.fecha_cfdi || x.fecha || '') + '">Ver en la tabla</button>'
    + '</span>';
}

/** La raya de un pago cuyo complemento aún no se lee, con su explicación para el mouse y para lector de pantalla. */
function sinMonto() {
  const s = sinImporte({ tipodecomprobante: 'P' });
  return '<span class="cell-cero" title="' + esc(s.titulo) + '">' + esc(s.texto) + '</span>'
    + '<span class="visually-hidden">' + esc(s.titulo) + '</span>';
}

function itemPago(p, moneda, sinCobros) {
  let h = '<li class="fd-arbol-item' + (cancelado(p) ? ' fd-arbol-cancelado' : '') + '">'
    + '<span class="fd-arbol-folio">' + esc(p.uuid ? folioDe(p) : 'Cobro registrado') + '</span>'
    + '<span>' + esc(fechaCorta(p.fecha)) + '</span>';
  // Sin el permiso de Cobranza el REP va sin monto (la nota del árbol dice por qué), no con la raya de «aún no se lee».
  if (sinCobros) return h + (cancelado(p) ? '<span class="badge bg-secondary">Cancelado</span>' : '') + acciones(p, 'P') + '</li>';
  h += '<span class="col-num">' + (vacio(p.monto) ? sinMonto() : esc(dinero(p.monto) + ' ' + moneda)) + '</span>';
  // Un pago cancelado no deja saldo: su «saldo después» ya no es el de la factura.
  if (!vacio(p.saldo) && !cancelado(p)) h += '<span class="fd-arbol-nota">Saldo después: ' + esc(dinero(p.saldo) + ' ' + moneda) + '</span>';
  if (!p.uuid) h += '<span class="fd-arbol-nota">Sin complemento de pago</span>';
  if (cancelado(p)) h += '<span class="badge bg-secondary">Cancelado</span>';
  return h + acciones(p, 'P') + '</li>';
}

function itemRelacionado(r, conEtiqueta) {
  let h = '<li class="fd-arbol-item' + (cancelado(r) ? ' fd-arbol-cancelado' : '') + '">';
  if (conEtiqueta) {
    const descripcion = propio(RELACION, r.tipo_relacion);
    h += '<span class="fd-arbol-rel"' + (descripcion ? ' title="' + esc(descripcion) + '"' : '') + '>' + esc(etiquetaRelacion(r)) + '</span>';
  }
  h += '<span class="fd-arbol-folio">' + esc(folioDe(r)) + '</span>'
    + '<span>' + esc(fechaCorta(r.fecha)) + '</span>'
    + '<span class="col-num">' + esc(dinero(r.monto) + (r.moneda ? ' ' + r.moneda : '')) + '</span>';
  if (cancelado(r)) h += '<span class="badge bg-secondary">Cancelado</span>';
  return h + acciones(r, r.tipo) + '</li>';
}

function seccion(titulo, items) {
  if (!items.length) return '';
  return '<div class="fd-arbol-seccion"><div class="fd-arbol-titulo">' + esc(titulo) + '</div>'
    + '<ul class="fd-arbol-lista">' + items.join('') + '</ul></div>';
}

/** El árbol de una factura (va en su línea hija). */
export function htmlArbol(arbol, fila) {
  const a = arbol || { pagos: [], notas: [], otros: [], saldo: null, moneda: 'MXN' };
  let h = '<div class="fd-arbol" role="group" aria-label="' + esc('Lo que pasó con ' + String((fila && fila.serfol) || 'esta factura')) + '">'
    + seccion('Pagos recibidos', a.pagos.map(function (p) { return itemPago(p, a.moneda, a.sinCobros); }))
    + seccion('Notas de crédito', a.notas.map(function (n) { return itemRelacionado(n, false); }))
    + seccion('Otros relacionados', a.otros.map(function (o) { return itemRelacionado(o, true); }));
  if (a.saldo !== null && a.saldo !== undefined) {
    h += '<div class="fd-arbol-saldo">' + (a.saldo > 0.005
      ? 'Saldo pendiente: <strong>' + esc(dinero(a.saldo) + ' ' + a.moneda) + '</strong>'
      : 'Sin saldo pendiente: esta factura ya está pagada.') + '</div>';
  }
  if (a.mas) h += '<div class="fd-arbol-pie">Y ' + esc(textoConteo(a.mas)) + ' más.</div>';
  if (a.sinCobros && a.pagos.length) h += '<div class="fd-arbol-pie">' + esc(NOTA_SIN_COBROS) + '</div>';
  return h + '</div>';
}

/** Filtros para «Ver en la tabla»: el UUID, su pestaña y el mes de su fecha (sin el mes, el periodo lo escondería). */
export function filtrosParaIr(item) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String((item && item.fecha) || ''));
  if (!m || !item.uuid) return null;
  const ultimo = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0)).getUTCDate();
  return {
    uuid: String(item.uuid),
    tipo: normalizarTipo(item.tipo) || 'todos',
    fechaini: m[1] + '-' + m[2] + '-01',
    fechafin: m[1] + '-' + m[2] + '-' + String(ultimo).padStart(2, '0'),
  };
}
